<template>
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form id="campaign-rule-set-form"
                autocomplete="new-password"
                @submit.stop.prevent="handleSubmit(saveEditData)"
                @reset="resetForm"
        >
            <dl class="description-list-wrapper">
                <DescriptionItem :label="$t('캠페인 규칙셋명')" mode="form" labelFor="edit-form-campaign-rule-set-name"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-campaign-rule-set-name"
                            :rules="{ required: true, min: 2 }"
                        >
                            <b-form-input id="edit-form-campaign-rule-set-name"
                                          v-model="form.name"
                                          name="edit-form-campaign-rule-set-name"
                                          :disabled="editLoading"
                                          :placeholder="$t('캠페인 규칙셋명')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-campaign-rule-set-name-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-campaign-rule-set-name-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>
                <DescriptionItem :label="$t('연결 프로젝트')" mode="form" labelFor="edit-form-project"
                                 required
                >
                    <div class="d-flex flex-column w-100">
                        <validation-provider v-slot="validationContext" name="edit-form-project" vid="edit-form-project"
                                             :rules="{ required: true }">

                            <div class="d-flex align-items-center rounded text-secondary w-100">
                                <auto-complete-select ref="projectCodeAutoComplete"
                                                      v-model="selectedProjectCodeId"
                                                      class="w-100"
                                                      :options="projectCodeOptions"
                                                      start-expanded
                                                      list-max-height="360px"
                                                      option-value-key="id"
                                                      :placeholder="$t('프로젝트를 선택해주세요.')"
                                                      :disabled="mode === 'edit'"
                                                      :state="getValidationState(validationContext)"
                                                      autocomplete="new-password"
                                                      @click="validationContext.validate()"
                                >
                                </auto-complete-select>
                            </div>
                            <b-form-invalid-feedback id="edit-form-project-feedback" class="d-block">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('연결 캠페인')" mode="form" labelFor="edit-form-campaign" required>

                    <div class="d-flex flex-column w-100">
                        <validation-provider v-slot="validationContext" name="edit-form-campaign" vid="edit-form-campaign"
                                             :rules="{ required: true }">

                            <div class="d-flex align-items-center rounded text-secondary w-100">
                                <auto-complete-select ref="projectCodeAutoComplete"
                                                      v-model="selectedCampaignId"
                                                      class="w-100"
                                                      :options="campaignOptions"
                                                      start-expanded
                                                      list-max-height="360px"
                                                      option-value-key="id"
                                                      :placeholder="$t('캠페인을 선택해주세요.')"
                                                      :disabled="mode === 'edit'"
                                                      :state="getValidationState(validationContext)"
                                                      autocomplete="new-password"
                                                      @click="validationContext.validate()"
                                >
                                </auto-complete-select>
                            </div>
                            <b-form-invalid-feedback id="edit-form-campaign-feedback" class="d-block">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('연결 규칙 그룹')" mode="form" labelFor="edit-form-rule-group"
                                 required
                >
                    <div class="w-100 d-flex justify-content-between">
                        <div class="w-100">
                            <validation-provider
                                v-slot="validationContext"
                                name="edit-form-rule-group"
                                :rules="{ required: true }"
                            >
                                <b-form-input id="edit-form-rule-group"
                                              :value="selectedRuleGroups?.[0]?.name || null"
                                              name="edit-form-rule-group"
                                              disabled
                                              :placeholder="$t('선택 버튼을 클릭하여 규칙 그룹을 선택해주세요.')"
                                              :state="getValidationState(validationContext)"
                                              autocomplete="new-password"
                                              aria-describedby="edit-form-rule-group-feedback"
                                />
                                <b-form-invalid-feedback id="edit-form-rule-group-feedback">
                                    {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                            </validation-provider>
                        </div>

                        <div class="ml-2">
                            <b-button :disabled="editLoading" @click="openRuleGroupSearchModal('form')">선택</b-button>
                        </div>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('횔성여부')" mode="form" labelFor="edit-form-is-active" required>
                    <validation-provider
                        v-slot="validationContext"
                        name="edit-form-is-active"
                        :rules="{ required: true }"
                    >
                        <b-form-radio-group
                            id="edit-form-is-active"
                            v-model="form.is_active"
                            :options="isActiveOption"
                            :button-variant="form.is_active ? 'outline-success' : 'outline-secondary'"
                            :state="getValidationState(validationContext)"
                            size="sm"
                            name="edit-form-is-active"
                            buttons
                            aria-describedby="edit-form-is-active-feedback"
                        />
                        <b-form-invalid-feedback id="edit-form-is-active-feedback"
                                                 class="d-block"
                                                 :class="{'mt-0': getValidationState(validationContext) !== false }"
                        >
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </validation-provider>
                </DescriptionItem>

                <DescriptionItem :label="$t('설명')" mode="form" labelFor="edit-form-description">
                    <b-form-textarea id="edit-form-description"
                                     v-model="form.description"
                                     :disabled="editLoading"
                                     :placeholder="$t('설명 (선택사항)')"
                                     autocomplete="new-password"
                    />
                </DescriptionItem>


                <div v-if="mode === 'edit'" class="row-wrapper">
                    <DescriptionItem :label="$t('등록일시')">
                        {{ get_date(detailData.created_at) || '-' }}
                    </DescriptionItem>

                    <DescriptionItem :label="$t('수정일시')">
                        {{ get_date(detailData.updated_at) || '-' }}
                    </DescriptionItem>
                </div>
            </dl>
        </b-form>

        <RuleGroupSearchModal :selected-data="selectedRuleGroupId"
                              type="radio"
                              @on-select="onSelectRuleGroup"
        >

        </RuleGroupSearchModal>
        <campaign-rule-set-error-modal :error-data="errData"
                                       @close="errData = null"
        />
    </validation-observer>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {createCampaignRuleSet, updateCampaignRuleSet} from '@shared_utils/api/ruleEngine';
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import RuleGroupSearchModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupSearchModal.vue';
import {isActiveOption} from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/data';
import CampaignRuleSetErrorModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetErrorModal.vue';
import {getDate} from '@shared/utils/common';
import {getCampaignOptions, getProjectCodeOptions} from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'CampaignRuleSetForm',
    components: {
        CampaignRuleSetErrorModal,
        RuleGroupSearchModal,
        AutoCompleteSelect,
        DescriptionItem,
    },
    props: {
        detailData: {
            type: [Object, null],
            default: null,
        },
        mode: {
            type: String,
            required: true,
            default: 'add', // add | edit
        },
        campaigns: {
            type: [Array, null],
            default: null,
        },
        projects: {
            type: [Array, null],
            default: null,
        }
    },

    data() {
        return {
            isProjectLoading: false,
            selectedProjectCodeId: null,

            isCampaignLoading: false,
            selectedCampaignId: null,

            selectedRuleGroups: [],

            editLoading: false,
            form: {
                name: null,
                description: null,
                is_active: true,
            },

            errData: null,
        };
    },
    computed: {
        get_date() {
            return (date) => {
                return getDate(date);
            };
        },
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        campaignOptions() {
            return getCampaignOptions(this.campaigns);
        },
        selectedRuleGroupId() {
            return this.selectedRuleGroups.map(item => item.id);
        },
        isActiveOption() {
            return isActiveOption;
        },
    },
    watch: {
        detailData: {
            immediate: true,
            handler() {
                this.form = {...this.form, ...this.detailData};
                if (this.detailData.root_rule_group) {
                    this.selectedRuleGroups = [this.detailData.root_rule_group];
                }
                if (this.detailData.project_id) {
                    this.selectedProjectCodeId = this.detailData.project_id;
                }
                if (this.detailData.campaign_id) {
                    this.selectedCampaignId = this.detailData.campaign_id;
                }
            }
        }
    },
    methods: {
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        async saveEditData() {
            try {
                this.errData = null;
                this.editLoading = true;

                let response = null;
                const form = {
                    ...this.form,
                    project_id: this.selectedProjectCodeId,
                    campaign_id: this.selectedCampaignId,
                    root_rule_group_id: this.selectedRuleGroupId?.[0]
                };
                if (this.mode === 'add') {
                    response = await createCampaignRuleSet(form);
                } else if (this.mode === 'edit') {
                    response = await updateCampaignRuleSet(this.detailData.id, form);
                }

                this.$EventBus.$emit('toast-success', this.$t('캠페인 규칙셋 등록 및 수정'), this.$t('캠페인 규칙셋이 성공적으로 저장되었습니다.'));
                this.$emit('updateDetailData', {...this.detailData, ...this.form, ...response.data});
            } catch (error) {
                console.error(error);
                console.dir(error);
                const status = error?.response?.status;
                const data = error?.response?.data;

                // 충돌 검증(409): 구조화 JSON → 전용 모달로 상세 표시.
                if (status === 409) {
                    this.errData = data;
                    this.$bvModal.show('campaign-rule-set-conflict-error-modal');
                    return;
                }

                // 4xx 검증 오류(예: 입력키 미등록 400)는 서버가 사람말 안내를 담아 보낸다.
                // 그대로 화면에 띄운다. JSON({message})이면 message 를, 평문(text/html)이면
                // 문자열 자체를 쓴다 — 백엔드 응답 형식이 바뀌어도 모두 처리한다.
                const serverMessage = getErrorMessage(error, '') || data?.msg;
                if (status >= 400 && status < 500 && serverMessage) {
                    this.$EventBus.$emit('toast-failure', this.$t('캠페인 규칙셋 등록 및 수정'), serverMessage);
                    return;
                }

                // 5xx / 알 수 없는 오류: 내부 메시지는 감추고 일반 안내만.
                this.$EventBus.$emit('toast-failure', this.$t('캠페인 규칙셋 등록 및 수정'), '캠페인 규칙셋 데이터 저장 도중 오류가 발생했습니다. 개발 팀에 문의해주세요.');
            } finally {
                this.editLoading = false;
            }
        },
        resetForm() {
            this.form = {
                name: null,
                description: null,
                is_active: true,
            };
        },
        onSelectRuleGroup(mapRuleGroup) {
            this.selectedRuleGroups = Object.values(mapRuleGroup);
        },
        openRuleGroupSearchModal() {
            this.$bvModal.show('rule-group-search-modal');
        },
    },
};
</script>

<style scoped lang="scss">

</style>
